import { AlertIcon, Input } from "@commonsku/styles";
import React, { useEffect, useRef, useState } from "react";
import { ItemVariant } from "../../../interfaces/ItemVariant";
import { useDispatch } from "react-redux";
import { updateVariant } from "../../../actions/item";
import ReactTooltip from "../../helpers/ReactTooltip";
import { useTooltipAttributes } from "../../../hooks";
import { rebuildTooltip } from "../../helpers";

interface ExternalSkuInputProps {
  variant: ItemVariant;
  isDuplicate: boolean;
  uniqueSkuError: string | null;
  onDuplicateChange: (variantId: string, externalId: string) => void;
  onErrorChange: (variantId: string, error: string | null) => void;
  onSuccess: (variantId: string) => void;
}

const ExternalSkuInput = ({
  variant,
  isDuplicate,
  uniqueSkuError,
  onDuplicateChange,
  onErrorChange,
  onSuccess,
}: ExternalSkuInputProps) => {
  const dispatch = useDispatch();
  const debounceRef = useRef(null);
  const useInputRef = useRef<string>(variant?.external_id || "");
  const [externalId, setExternalId] = useState<string>(
    variant?.external_id || ""
  );

  useEffect(() => {
    rebuildTooltip();
  }, [uniqueSkuError, isDuplicate]);

  const handleUpdateExternalSku = (e: { target: { value: string } }) => {
    const newExternalSku = e.target.value;
    setExternalId(newExternalSku);
    useInputRef.current = newExternalSku;

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      (dispatch as any)(
        updateVariant(
          variant?.item_id,
          variant?.item_variant_id,
          useInputRef.current
        )
      )
        .then(() => {
          onDuplicateChange(variant?.item_variant_id, useInputRef.current);
          onErrorChange(variant?.item_variant_id, null);
          onSuccess(variant?.item_variant_id);
        })
        .catch((err) => {
          console.log(err);
          onErrorChange(
            variant?.item_variant_id,
            err?.json?.errors[0] || "Error updating SKU"
          );
        });
    }, 1000);
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const currentExternalId = variant?.external_id || "";
    if (useInputRef.current !== currentExternalId) {
      setExternalId(useInputRef.current);
    }
  }, [variant?.external_id]);

  const variantError = uniqueSkuError;
  const toolTipId = `sku-error-tooltip-${variant?.item_variant_id}`;
  const toolTipAttributes = useTooltipAttributes(variantError, toolTipId, {
    place: "bottom",
  });

  return (
    <div
      style={{ position: "relative", display: "flex", alignItems: "center" }}
      {...toolTipAttributes}
    >
      <Input
        name="externalSkuInput"
        value={externalId}
        style={{
          marginBottom: 0,
          borderRadius: "5px",
          borderColor: variantError ? "red" : null,
        }}
        onChange={handleUpdateExternalSku}
        error={!!variantError}
        readOnly={!!variant.locked}
      />
      {variantError && (
        <>
          <ReactTooltip id={`sku-error-tooltip-${variant?.item_variant_id}`} />
          <AlertIcon
            data-tooltip-id={toolTipId}
            data-tooltip-content={uniqueSkuError || "SKUs have to be unique"}
            style={{
              marginLeft: 8,
              cursor: "pointer",
              color: "red",
              width: "20px",
              height: "20px",
              position: "absolute",
              right: 10,
            }}
            altText=""
          />
        </>
      )}
    </div>
  );
};

export default ExternalSkuInput;
